<template>
    <Widget :title="$t('components.AppointmentsStats.Title')" :info="$t('widgetsInfo.AppointmentsStats')" :loading="loading" :report-mode="reportMode"> 
        <v-row align="center" justify="space-between" style="width: 100%">
            <v-col cols="12" sm="4">
                <span class="d-inline-block mx-4 text-caption ddwhite--text" v-for="(item, index) in Object.values(legend)" :key="index">
                    <span class="circle" :style="{ backgroundColor: item.color }"></span> 
                    <span class="label">{{ item.title }}</span>
                </span>
            </v-col>

            <v-col cols="12" sm="4" class="text-right" v-if="!isReport">
                <span class="d-block input-label mr-6 ddgrey--text text-caption">
                    {{ $t("components.AppointmentsStats.FilterText") }}
                </span>
                <v-select
                    solo
                    dense
                    class="d-inline-block groupby"
                    v-model="groupBy"
                    item-text="hint"
                    item-value="value"
                    :items="[
                        { value: 'all', hint: $t('components.AppointmentsStats.AllGroupsLabel') },
                        { value: 'booked', hint: $t('components.AppointmentsStats.BookedGroupLabel') },
                        { value: 'showed', hint: $t('components.AppointmentsStats.ShowedGroupLabel') },
                        { value: 'noshow', hint: $t('components.AppointmentsStats.NoShowGroupLabel') },
                        { value: 'cancelled', hint: $t('components.AppointmentsStats.CancelledGroupLabel') },
                        { value: 'deleted', hint: $t('components.AppointmentsStats.DeletedGroupLabel') },
                        { value: 'confirmed', hint: $t('components.AppointmentsStats.ConfirmedGroupLabel') },
                    ]"
                    :label="$t('components.AppointmentsStats.FilterText')"
                    @change="filterData(data)"/>
            </v-col>
        </v-row>
        <StackedBarChart :stats="stats" :styles="styles"/>
    </Widget>
</template>

<script>
/** 
 * AppointmentsStats wraps a StackedBarChart and contains the functions to parse
 * and format data from the API.
 * 
 * It represents the number of chats versus the number of referrals grouped
 * by month, day of the week or hour of the day.
 */

import Widget from '@/components/dashboard/Widget';

import StackedBarChart from '@/components/dashboard/charts/StackedBarChart';

export default {
    name: 'AppointmentsStats',
    data: () => ({ 
        stats: {},
        styles: {
            position: 'relative',
            width: '100%',
            padding: '24px 24px 0',
        },
        groupBy: 'all',
        legend: [],
    }),
    props: {
        loading: {
            type: Boolean,
            default: true
        },
        initialData: {
            type: Object,
            required: true
        },
        reportMode: {
            type: String,
            default: null
        },
    },
    computed: {
        isReport() { return this.reportMode !== null },
        colors() {
            return this.$vuetify.theme.isDark ?
                this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;
        }
    },
    mounted() {
        this.inflateLegend();
        this.updateStats(this.data);
        console.log("STATS mounted");
    },
    watch: {
        initialData(newData) {
            this.updateStats(newData);
        },
        groupBy() {
            this.updateStats(this.initialData);
        },
        filterBy() {
            this.updateStats(this.initialData);
        }
    },
    methods: {
        inflateLegend() {
            const $t = this.$t.bind(this);
            this.legend = {
                booked: {
                    title: $t("components.AppointmentsStats.BookedGroupLabel"),
                    color: this.colors.ddlightblue
                },
                showed: {
                    title: $t("components.AppointmentsStats.ShowedGroupLabel"),
                    color: this.colors.ddlightgreen
                },
                noshow: {
                    title: $t("components.AppointmentsStats.NoShowGroupLabel"),
                    color: this.colors.ddgrey
                },
                cancelled: {
                    title: $t("components.AppointmentsStats.CancelledGroupLabel"),
                    color: this.colors.ddlightdark
                },
                deleted: {
                    title: $t("components.AppointmentsStats.DeletedGroupLabel"),
                    color: this.colors.ddlightgrey
                },
                confirmed: {
                    title: $t("components.AppointmentsStats.ConfirmedGroupLabel"),
                    color: this.colors.ddgreen
                },
            }
        },
        updateStats(data) {
            if (data) {
                const items = this.filterData(data);
                this.stats = this.formatItems(items);
            }
        },
        filterData(data) {
            const items = data.by_month;
            if (items) {
                let formattedItems = {};
                const labels = Object.keys(items);
                const keys = Object.keys(items);
                const { length } = keys;
                for (let i = 0; i < length; i++) {
                    const key = keys[i];
                    const label = labels[i];
                    formattedItems[label] = items[key];
                }
                return formattedItems;
            }
            return {};
        },
        formatItems(items) {
            const $t = this.$t.bind(this);

            const labels = [...Object.keys(items)];
            const datasets = [];
            const booked = {
                barPercentage: 0.5,
                label: $t("components.AppointmentsStats.BookedGroupLabel"),
                backgroundColor: this.colors.ddlightblue,
                data: []
            }
            const showed = {
                barPercentage: 0.5,
                label: $t("components.AppointmentsStats.ShowedGroupLabel"),
                backgroundColor: this.colors.ddlightgreen,
                data: []
            }
            const noshow = {
                barPercentage: 0.5,
                label: $t("components.AppointmentsStats.NoShowGroupLabel"),
                backgroundColor: this.colors.ddgrey,
                data: []
            }
            const cancelled = {
                barPercentage: 0.5,
                label: $t("components.AppointmentsStats.CancelledGroupLabel"),
                backgroundColor: this.colors.ddlightdark,
                data: []
            }
            const deleted = {
                barPercentage: 0.5,
                label: $t("components.AppointmentsStats.DeletedGroupLabel"),
                backgroundColor: this.colors.ddlightgrey,
                data: []
            }
            const confirmed = {
                barPercentage: 0.5,
                label: $t("components.AppointmentsStats.ConfirmedGroupLabel"),
                backgroundColor: this.colors.ddgreen,
                data: []
            }

            const { length } = labels;
            for (let i = 0; i < length; i++) {
                const item = items[labels[i]];
                booked.data.push(item.booked);
                showed.data.push(item.showed);
                noshow.data.push(item.noshow);
                cancelled.data.push(item.cancelled);
                deleted.data.push(item.deleted);
                confirmed.data.push(item.confirmed);
            }

            switch (this.groupBy) {
                case 'booked':
                    datasets.push(booked);
                    break;
                case 'showed':
                    datasets.push(showed);
                    break;
                case 'noshow':
                    datasets.push(noshow);
                    break;
                case 'cancelled':
                    datasets.push(cancelled);
                    break;
                case 'deleted':
                    datasets.push(deleted);
                    break;
                case 'confirmed':
                    datasets.push(confirmed);
                    break;
                case 'all':
                    datasets.push(booked);
                    datasets.push(showed);
                    datasets.push(noshow);
                    datasets.push(cancelled);
                    datasets.push(deleted);
                    datasets.push(confirmed);
                    break;
            }

            return { labels, datasets };
        }
    },
    components: { Widget, StackedBarChart }
}
</script>

<style>
    .circle {
        display: inline-block;
        vertical-align: top;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin: 4px 12px;
    }

    .label {font-size:  1.2em !important;}
    .input-label.input-label {font-size:  1.02em !important;}
    
    .groupby .v-text-field__details { display: none; }
</style>